// The inspection record's Aircraft List tab (H98): one row per airframe that
// carries this inspection — legacy's aircraft_templates_tab_aircraft_list.inc.
//
// BP-80, the same RecordTabGrid as its sibling Config List: (+) · ACTIONS in the
// grid's own toolbar, no row pencils. (+) links more aircraft (Aaron 2026-10-06,
// Peak inspection 2554: "need to be able to link other aircraft same as
// config") through LinkAircraftDialog -> POST /v6/inspection/aircraft-list/{id};
// ACTIONS -> Delete soft-deletes the selected rows
// (DELETE /v6/inspection/limitations/{id}).
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { Button, IconButton } from '@mui/material'
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import type { GridColDef } from '@mui/x-data-grid-premium'

import LightTooltip from '../components/LightTooltip'
import AlertDialog from '../components/AlertDialog'
import type { ActionMenuEntry } from '../components/datagrid/ActionMenu'
import type { BasedOnChoice } from '../components/LimitationFields'
import LinkAircraftDialog from '../inspections-tr/dialogs/LinkAircraftDialog'
import { deleteInspectionLimitation, getInspectionAircraftList } from '../inspections-tr/inspections_tr'
import RecordTabGrid, { columnSortValueOf } from '../inspections-tr/tabs/RecordTabGrid'
import { REMAINING_COLUMN } from '../inspections-tr/tabs/InspectionsTRLimitations'
import { EMDASH, recordedDate } from '../inspections-tr/recordedDate'
import type { InspectionAircraftListRow } from '../inspections-tr/types'
import { InspectionDetailView } from './types'
import { limitationWarningLabel, limitationWarningRowClassName, limitationWarningRowSx } from "../inspections-tr/limitationWarning";

interface InspectionLimitationsProps {
  originalUrl: string;
  formRef?: any;
  inspection: InspectionDetailView;
}

const BASED_ON_DATE = ['Date (Months)', 'Date (Days)', 'Date (Years)'];

const COLUMNS: GridColDef<InspectionAircraftListRow>[] = [
  // H98 — legacy's Aircraft List leads with the airframe
  // (aircraft_templates_tab_aircraft_list.inc:28). The record header already
  // says which inspection this is.
  {
    headerName: "Aircraft",
    field: "registration",
    flex: 1,
    renderCell: (params) => (
      <span title={limitationWarningLabel(params.row)}>{params.row?.registration ?? "—"}</span>
    ),
  },
  { headerName: "Based On", field: "based_on", flex: 1 },
  {
    headerName: "Period",
    field: "period",
    flex: 1,
    valueGetter: (_value, row) => {
      const period = Number(row?.period || 0.0)
      return period - Math.floor(period) === 0 ? Math.floor(period) : period
    },
  },
  {
    headerName: "Warning",
    field: "warning",
    flex: 1,
    valueGetter: (_value, row) => {
      const warning = Number(row?.warning || 0.0)
      return warning - Math.floor(warning) === 0
        ? `${Math.floor(warning)} ` + row?.warning_unit || ""
        : warning + (row?.warning_unit ?? "")
    },
  },
  {
    // A date axis with nothing recorded — a freshly linked row (NULL, version-23)
    // or a stored zero-date — reads "—" (Aaron 2026-10-06), never a blank or a date.
    headerName: "Last Done",
    field: "last_inspections",
    flex: 1,
    valueGetter: (_value, row) =>
      BASED_ON_DATE.includes(row?.based_on ?? "") ? recordedDate(row?.last_date) ?? EMDASH : row?.last_units || 0.0,
  },
  {
    headerName: "Next Due",
    field: "next_inspection",
    flex: 1,
    valueGetter: (_value, row) =>
      BASED_ON_DATE.includes(row?.based_on ?? "") ? recordedDate(row?.next_date) ?? EMDASH : row?.next_units || "",
  },
  // The ONE remaining rule both Aircraft List surfaces use (resolveRemaining):
  // an em dash when nothing is recorded, red when overdue, closest first when
  // sorted. Was `days_remaining || units_remaining || 0` — a not-recorded
  // remaining read 0, i.e. "due now", and a sort ordered one page.
  REMAINING_COLUMN,
  {
    headerName: "Notes",
    field: "limitaitons.limitation_notes",
    flex: 0.5,
    sortable: false,
    renderCell: (params) => (
      <LightTooltip title={params.row?.limitation_notes || ""}>
        <IconButton>
          <DescriptionOutlinedIcon />
        </IconButton>
      </LightTooltip>
    ),
  },
];

// Every column sorts over the whole list, not one page (RecordTabGrid).
const SORT_VALUE_OF = columnSortValueOf(COLUMNS);

const InspectionComponent: React.FC<InspectionLimitationsProps> = ({ inspection }) => {
  const { enqueueSnackbar } = useSnackbar()
  const inspectionId = Number(inspection?.id)
  const [refreshToken, setRefreshToken] = useState(0)
  const refresh = useCallback(() => setRefreshToken((t) => t + 1), [])
  const [linkOpen, setLinkOpen] = useState(false)
  const [deleting, setDeleting] = useState<InspectionAircraftListRow[]>([])
  // Every row the last read returned (the grid holds one page): what (+) must
  // not offer again, and the Based On values this inspection already uses.
  const allRows = useRef<InspectionAircraftListRow[]>([])

  const load = useCallback(async () => {
    // H98: the aircraft-list route, not /limitations — same rows minus
    // model-sourced and retired-airframe ones (legacy's own filter), plus the
    // registration and legacy's merged warning colour.
    const rows = await getInspectionAircraftList(inspectionId)
    allRows.current = rows
    return rows
  }, [inspectionId])

  const basedOnOptions = useMemo<BasedOnChoice[]>(() => {
    const values = [inspection?.based_on, ...allRows.current.map((r) => r.based_on)]
      .filter((v): v is string => !!v)
    return Array.from(new Set(values)).map((v) => ({ value: v, label: v }))
    // allRows is read when the dialog opens, after the grid has loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection?.based_on, linkOpen])

  const linkedAircraftIds = useMemo(
    () => allRows.current.map((r) => r.aircraft_id).filter((id): id is number => !!id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linkOpen]
  )

  const actions = useMemo<ActionMenuEntry[]>(() => [
    {
      name: "Delete",
      namePlural: "Delete",
      action: ["Delete", "DELETE"],
      requiresSelection: true,
      multipleSelection: true,
      isReturnDataSelection: true,
      onClick: (payload: any) => {
        const rows: InspectionAircraftListRow[] = payload?.selectedRows ? Array.from(payload.selectedRows) : []
        if (rows.length) setDeleting(rows)
      },
    },
  ], [])

  const confirmDelete = async () => {
    const rows = deleting
    setDeleting([])
    for (const row of rows) {
      try {
        await deleteInspectionLimitation(row.id)
        enqueueSnackbar(`Limitation ${row.id} deleted`, { variant: "success" })
      } catch {
        enqueueSnackbar(`Error deleting limitation ${row.id}`, { variant: "error" })
      }
    }
    refresh()
  }

  return (
    <>
      <RecordTabGrid<InspectionAircraftListRow>
        name="inspection-record-aircraft-list"
        what="Aircraft List"
        testId="inspection-aircraft-list-tab"
        load={load}
        columns={COLUMNS}
        noRowsLabel="No Limitation Added"
        // H98 — legacy colours an Aircraft List row's text by its warning
        // (red overdue, blue coming due, green extension held).
        getRowClassName={(params) => limitationWarningRowClassName(params.row)}
        sx={limitationWarningRowSx}
        onCreate={() => setLinkOpen(true)}
        actions={actions}
        refreshToken={refreshToken}
        sortValueOf={SORT_VALUE_OF}
      />
      <LinkAircraftDialog
        open={linkOpen}
        inspectionId={inspectionId}
        linkedAircraftIds={linkedAircraftIds}
        basedOnOptions={basedOnOptions}
        onClose={() => setLinkOpen(false)}
        onLinked={() => {
          setLinkOpen(false)
          refresh()
        }}
      />
      <AlertDialog
        title={deleting.length > 1 ? `Delete these ${deleting.length} records?` : "Delete this record?"}
        open={deleting.length > 0}
        handleClose={() => setDeleting([])}
      >
        <Button onClick={() => setDeleting([])} color="primary">
          No
        </Button>
        <Button onClick={confirmDelete} color="primary" autoFocus>
          Yes
        </Button>
      </AlertDialog>
    </>
  )
}

export default InspectionComponent
